import { accessibleFacilityIds, withFrontDeskReach, type PolicySubject } from "@alm/policy";

const ALL = "all";

/* The phone app writes its building here just before a page of the site loads
   (apps/mobile/lib/app/site-prelude.ts). It is a hand-over, not a home: read once
   into the person's own key and removed, so nobody inherits it later. */
const HANDED_OVER_KEY = "alm.selectedFacilityId";

/** Kept per person: on a shared PC the next person to sign in starts from their own choice. */
export function facilityStorageKey(userId: string): string {
  return `${HANDED_OVER_KEY}:${userId}`;
}

/** A building this person may look at, as the switcher lists them (their front desk roster
 * buildings included), or "all". A building chosen as somebody else, or taken away since, is
 * refused on every screen and left the switcher blank, so it falls back to "all of mine". */
export function knownFacilityChoice(stored: string, subject: PolicySubject): string {
  if (stored === ALL) return ALL;
  const reach = accessibleFacilityIds(withFrontDeskReach(subject));
  return reach === null || reach.includes(stored) ? stored : ALL;
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** What this person last chose on this browser, unchecked; "all" when nothing is kept. */
export function readStoredFacility(subject: PolicySubject): string {
  const store = storage();
  if (!store) return ALL;
  const key = facilityStorageKey(subject.userId);
  try {
    const handedOver = store.getItem(HANDED_OVER_KEY);
    if (handedOver !== null) {
      store.removeItem(HANDED_OVER_KEY);
      store.setItem(key, handedOver);
    }
    return store.getItem(key) ?? ALL;
  } catch {
    return ALL;
  }
}

export function storeFacility(subject: PolicySubject, id: string): void {
  try {
    storage()?.setItem(facilityStorageKey(subject.userId), id);
  } catch {
    // Unsaved, the choice still holds for this tab.
  }
}

/** The building the screens are showing, or null for all of the person's buildings. */
export function chosenFacility(subject: PolicySubject): string | null {
  const choice = knownFacilityChoice(readStoredFacility(subject), subject);
  return choice === ALL ? null : choice;
}
